<template>
  <div v-if="i18nReady" class="sp-translation-toolbar" :class="classModifiers" part="base">
    <translation-toolbar-language-menu
      :default-locale="defaultLocale"
      :default-language-name="defaultLanguageName"
      :languages="languages"
    ></translation-toolbar-language-menu>

    <translation-toolbar-clara-picker
      v-if="offersClara"
      :default-language-name="defaultLanguageName"
      :languages="languages"
      :resource-type="resourceType"
      :resource-id="resourceId"
      :translate-url="translateUrl"
      @translations-requested="forwardTranslationsRequested"
    ></translation-toolbar-clara-picker>
  </div>
</template>

<script setup>
/**
 * The header toolbar of an admin edit page whose record has translatable fields: a language
 * selector that switches every `sp-translatable-field` on the page at once, and, given a
 * `translate-url`, a Clara action that fills the fields and languages an editor picks. The fields
 * report themselves through the shared translation page store.
 *
 * @displayName Translation Toolbar
 * @group Custom Elements
 * @component sp-translation-toolbar
 */
import { computed, ref } from "vue";

import { props as translationToolbarProps } from "./props.js";
import { whenI18nReady } from "./i18n";
import TranslationToolbarClaraPicker from "./TranslationToolbarClaraPicker.ce.vue";
import TranslationToolbarLanguageMenu from "./TranslationToolbarLanguageMenu.ce.vue";

const props = defineProps(translationToolbarProps);

const emit = defineEmits(["translations-requested"]);

const i18nReady = ref(false);

function markI18nReady() {
  i18nReady.value = true;
}

whenI18nReady.then(markI18nReady);

const languages = computed(() => {
  if (Array.isArray(props.languages)) {
    return props.languages;
  }

  try {
    const parsed = JSON.parse(props.languages || "[]");

    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
});

const defaultLanguageName = computed(() => props.defaultLanguageName ?? props.defaultLocale);

const offersClara = computed(() => Boolean(props.translateUrl) && languages.value.length > 0);

function forwardTranslationsRequested(detail) {
  emit("translations-requested", detail);
}

const classModifiers = computed(() => ({ "--offers-clara": offersClara.value }));
</script>

<style>
:host {
  display: inline-block;
  /* Shield from an ancestor's --sp-comp-menu-* popup tokens so the language and picker popups are
     sized by the toolbar. */
  --sp-comp-menu-width: initial;
  --sp-comp-menu-min-width: initial;
  --sp-comp-menu-content-padding: initial;
}
</style>

<style lang="scss" scoped>
.sp-translation-toolbar {
  --toolbar-gap: var(--sp-comp-translation-toolbar-gap, var(--sp-ref-scale-8));

  display: flex;
  align-items: center;
  gap: var(--toolbar-gap);
}
</style>
